'use client';

import type { FunctionComponent } from 'react';

import { useTranslations } from '@power-core/toolkit';

import { AbbCheckMark } from '@/assets/icons/components/abb-check-mark';
import { AbbNotAllowed } from '@/assets/icons/components/abb-not-allowed';
import { useInvitationsInit } from '@/modules/shared-document/hooks/use-invitations-init';
import { useSharedDocumentsApi } from '@/modules/shared-document/hooks/use-shared-documents-api';
import { useDate } from '@/modules/utils/custom-hooks/use-date';
import { getInitials } from '@/modules/utils/get-initials';
import { Avatar } from '@/ui-components/avatar/avatar';
import { Button } from '@/ui-components/button/button';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { LoadingIndicator } from '@/ui-components/loading-indicator/loading-indicator';
import { Paragraph } from '@/ui-components/paragraph/paragraph';

export const Invitations: FunctionComponent<{ isOpen: boolean }> = ({ isOpen }) => {
	const t = useTranslations();
	const { documentAccesses, isPending } = useInvitationsInit({ isOpen });
	const { acceptAccess, leaveSharedDocument, isPending: isAcceptAccessPending } = useSharedDocumentsApi();
	const { format } = useDate();

	if (!isOpen) return null;

	if (isPending) {
		return (
			<Flexbox justify='center' className='mt-5 mb-5'>
				<LoadingIndicator sizing='large' color='primary' />
			</Flexbox>
		);
	}

	if (documentAccesses && documentAccesses.length > 0) {
		return documentAccesses.map((documentAccess) => {
			return (
				<Flexbox key={documentAccess.documentId} justify='between' className='w-full'>
					<Flexbox className='px-2'>
						<Avatar sizing='small' rounded placeholderInitials={getInitials(documentAccess.ownerName ?? '')} />
						<Flexbox direction='col' items='start' className='ml-2'>
							<Paragraph variant='text-sm-thin'>
								<span className='font-semibold'>{documentAccess.ownerName}</span>
								{` ${t('T_USER_SHARED_DOCUMENT')}`}
							</Paragraph>
							<Paragraph variant='text-sm-thin' className='text-n500_light_text'>
								<span className='text-md-thin'>{`${t('T_PROJECT')}: `}</span>
								{documentAccess.projectDocument?.name}
							</Paragraph>
							<Paragraph variant='text-sm-thin' className='text-n500_light_text'>
								<span className='text-md-thin'>{`${t('T_EXPIRE_AT')}: `}</span>
								{documentAccess.unlimited ? t('T_UNLIMITED_INVITATION') : `${format(documentAccess.expiresAt, 'DD/MM/YYYY')}`}
							</Paragraph>
						</Flexbox>
					</Flexbox>

					<Flexbox items='center' justify='center' className='gap-2'>
						<Button
							icon={<AbbCheckMark fill={'#ffffff'} size={20} />}
							sizing='small'
							iconPosition='left'
							variant='secondary'
							className='w-28'
							disabled={!!documentAccess.isAccepted}
							isLoading={isAcceptAccessPending}
							onClick={() => acceptAccess.mutate({ accessDocumentId: documentAccess.documentId })}
						>
							{documentAccess.isAccepted ? t('T_ACCEPTED') : t('T_ACCEPT')}
						</Button>

						<Button
							icon={<AbbNotAllowed fill={'#ffffff'} size={20} />}
							sizing='small'
							iconPosition='left'
							variant={'primary'}
							className='w-28'
							isLoading={isAcceptAccessPending}
							onClick={() => leaveSharedDocument.mutate({ accessDocumentId: documentAccess.documentId })}
						>
							{documentAccess.isAccepted ? t('T_LEAVE') : t('T_REJECT')}
						</Button>
					</Flexbox>
				</Flexbox>
			);
		});
	}

	return (
		<Flexbox items='center' justify='between' className='p-2'>
			<Paragraph className='text-n500_light_text'>{t('T_NO_SHARING_DOCUMENTS_YET')}</Paragraph>
		</Flexbox>
	);
};
